import {
  PaymentMethodEnum,
  ReceiptTypeEnum,
  IReceipt,
  IReceiptItem,
} from "@prostocheck/shared";

import {
  parseDocumentXml,
  readBoolean,
  readNumber,
  readPaymentMethod,
  readRows,
  readText,
  toIsoTimestamp,
  toMoney,
} from "../document-xml";
import {
  OFFLINE_FISCAL_NUMBER_PATTERN,
  RECEIPT_TYPE_BY_SUB_TYPE,
} from "./receipt-xml.constants";
import { IParseReceiptParams } from "./receipt-xml.models";
import {
  toDominantPaymentRow,
  toLineVatRate,
  toTaxRows,
} from "./receipt-xml.utils";

/**
 * Maps a `<CHECK>` document onto `IReceipt`. Nothing read here is translated:
 * item names, the cashier and the outlet are ДПС data and stay as written.
 */
export const parseReceipt = ({
  data,
  fiscalNumber,
}: IParseReceiptParams): IReceipt => {
  const document = parseDocumentXml(data);
  const head = document.querySelector("CHECKHEAD") ?? document;
  const total = document.querySelector("CHECKTOTAL");

  const taxRows = toTaxRows(readRows(document, "CHECKTAX"));
  const dominantPayment = toDominantPaymentRow(readRows(document, "CHECKPAY"));

  // The server assigns ORDERTAXNUM; a document fetched by fiscal number always
  // has one, but an offline document carries the reserved number instead.
  const documentFiscalNumber = readText(head, "ORDERTAXNUM") || fiscalNumber;

  return {
    fiscalNumber: documentFiscalNumber,
    cashRegisterFn: readText(head, "CASHREGISTERNUM"),
    localNumber: readNumber(head, "ORDERNUM"),
    type:
      RECEIPT_TYPE_BY_SUB_TYPE[readText(head, "DOCSUBTYPE")] ??
      ReceiptTypeEnum.Sale,
    issuedAt: toIsoTimestamp({
      date: readText(head, "ORDERDATE"),
      time: readText(head, "ORDERTIME"),
    }),
    paymentMethod: dominantPayment
      ? readPaymentMethod(dominantPayment)
      : PaymentMethodEnum.Cash,
    total: toMoney(total ? readNumber(total, "SUM") : 0),
    vatTotal: toMoney(
      taxRows
        .filter(row => row.isVat)
        .reduce((sum, row) => sum + row.amount, 0),
    ),
    items: readRows(document, "CHECKBODY").map(row =>
      toReceiptItem({ row, taxRows }),
    ),
    isOffline:
      readBoolean(head, "OFFLINE") ||
      OFFLINE_FISCAL_NUMBER_PATTERN.test(documentFiscalNumber),
  };
};

const toReceiptItem = ({
  row,
  taxRows,
}: {
  row: Element;
  taxRows: ReturnType<typeof toTaxRows>;
}): IReceiptItem => ({
  name: readText(row, "NAME"),
  quantity: readNumber(row, "AMOUNT"),
  price: toMoney(readNumber(row, "PRICE")),
  total: toMoney(readNumber(row, "COST")),
  vatRate: toLineVatRate({ letters: readText(row, "LETTERS"), taxRows }),
});
